/**
 * Public API surface.
 *
 * Each function picks the mock adapter or a real HTTP call based on
 * VITE_USE_MOCKS. Components and hooks only ever import from here, so turning
 * the FastAPI service on is a configuration change rather than a refactor.
 *
 * Endpoint paths below are the contract /server should implement.
 */
import { env } from "@/lib/env";
import { get, post } from "@/lib/http";
import { toParams } from "./queries";
import type { BlogQuery, JobQuery, PlanQuery, ProjectQuery, SearchQuery, TurnkeyQuery } from "./queries";
import type { SiteFacets } from "./facets";
import type {
  ApplicationPayload,
  BlogPost,
  CheckoutPayload,
  CompletedProject,
  EnquiryPayload,
  FloorPlan,
  Job,
  Licence,
  SearchResult,
  Service,
  SubmissionResult,
  TurnkeyProject,
} from "@/types";

/**
 * The mock adapter (and the ~170 KB catalogue it imports) is loaded on demand,
 * so it never enters the initial bundle and disappears from the critical path
 * the moment VITE_USE_MOCKS is false.
 */
type MockModule = typeof import("./mock");
let mockPromise: Promise<MockModule> | undefined;
const mock = (): Promise<MockModule> => (mockPromise ??= import("./mock"));

/** Renamed off a `use` prefix so it is not mistaken for a React hook. */
const mocksEnabled = () => env.useMocks;

/* ------------------------------------------------------- completed projects */
export const projectsApi = {
  list: (query: ProjectQuery = {}): Promise<CompletedProject[]> =>
    mocksEnabled() ? mock().then((m) => m.listProjects(query)) : get("/projects", { params: toParams(query) }),

  byId: (id: string): Promise<CompletedProject> =>
    mocksEnabled() ? mock().then((m) => m.getProject(id)) : get(`/projects/${id}`),
};

/* ------------------------------------------------------------------ turnkey */
export const turnkeyApi = {
  list: (query: TurnkeyQuery = {}): Promise<TurnkeyProject[]> =>
    mocksEnabled() ? mock().then((m) => m.listTurnkey(query)) : get("/turnkey", { params: toParams(query) }),

  byId: (id: string): Promise<TurnkeyProject> =>
    mocksEnabled() ? mock().then((m) => m.getTurnkey(id)) : get(`/turnkey/${id}`),
};

/* -------------------------------------------------------------- floor plans */
export const plansApi = {
  list: (query: PlanQuery = {}): Promise<FloorPlan[]> =>
    mocksEnabled() ? mock().then((m) => m.listPlans(query)) : get("/floor-plans", { params: toParams(query) }),

  /**
   * A licence is passed as a *claim*, not as proof. The server looks it up and
   * returns the locked payload if it does not check out, so sending a made-up
   * one reveals nothing — which is why it is safe to send from the browser.
   */
  byId: (id: string, licence?: Licence): Promise<FloorPlan> =>
    mocksEnabled()
      ? mock().then((m) => m.getPlan(id))
      : get(`/floor-plans/${id}`, {
          params: licence ? { licenceId: licence.licenceId, email: licence.email } : undefined,
        }),

  /**
   * Creates the licence. The server is the only party allowed to mint one —
   * the client stores the response, it does not generate it.
   */
  purchase: (payload: CheckoutPayload): Promise<Licence> =>
    mocksEnabled() ? mock().then((m) => m.purchaseLicence(payload)) : post("/floor-plans/purchase", payload),

  /**
   * Single-use, short-lived download URL, issued only after the server
   * re-verifies the licence against the database. Not reachable with mocks on.
   */
  downloadUrl: (
    planId: string,
    format: string,
    licence: Licence,
  ): Promise<{ url: string; expiresAt: string; expiresIn: number; format: string }> =>
    mocksEnabled()
      ? Promise.reject(new Error("Downloads require the backend"))
      : get(`/floor-plans/${planId}/download`, {
          params: { format, licenceId: licence.licenceId, email: licence.email },
        }),
};

/* -------------------------------------------------------------------- blogs */
export const blogApi = {
  list: (query: BlogQuery = {}): Promise<BlogPost[]> =>
    mocksEnabled() ? mock().then((m) => m.listBlogPosts(query)) : get("/journal", { params: toParams(query) }),

  byId: (id: string): Promise<BlogPost> =>
    mocksEnabled() ? mock().then((m) => m.getBlogPost(id)) : get(`/journal/${id}`),
};

/* ----------------------------------------------------------------- services */
export const servicesApi = {
  list: (): Promise<Service[]> => (mocksEnabled() ? mock().then((m) => m.listServices()) : get("/services")),
  byId: (id: string): Promise<Service> =>
    mocksEnabled() ? mock().then((m) => m.getService(id)) : get(`/services/${id}`),
};

/* ------------------------------------------------------------------ careers */
export const jobsApi = {
  list: (query: JobQuery = {}): Promise<Job[]> =>
    mocksEnabled() ? mock().then((m) => m.listJobs(query)) : get("/careers", { params: toParams(query) }),

  byId: (id: string): Promise<Job> => (mocksEnabled() ? mock().then((m) => m.getJob(id)) : get(`/careers/${id}`)),

  apply: (payload: ApplicationPayload): Promise<SubmissionResult> =>
    mocksEnabled() ? mock().then((m) => m.submitApplication(payload)) : post("/careers/apply", payload),
};

/* ------------------------------------------------------------------- search */
export const searchApi = {
  query: (query: SearchQuery = {}): Promise<SearchResult[]> =>
    mocksEnabled() ? mock().then((m) => m.search(query)) : get("/search", { params: toParams(query) }),
};

/* -------------------------------------------------------------------- forms */
export const contactApi = {
  enquire: (payload: EnquiryPayload): Promise<SubmissionResult> =>
    mocksEnabled() ? mock().then((m) => m.submitEnquiry(payload)) : post("/enquiries", payload),

  subscribe: (email: string): Promise<SubmissionResult> =>
    mocksEnabled() ? mock().then((m) => m.subscribe(email)) : post("/newsletter", { email }),
};

/* -------------------------------------------------------------------- meta */
export const metaApi = {
  /**
   * Option lists, counts and headline totals for the listing pages.
   * Cached indefinitely — it only changes when the catalogue does.
   */
  facets: (): Promise<SiteFacets> =>
    mocksEnabled() ? mock().then((m) => m.getFacets()) : get("/meta/facets"),
};

export { ApiError } from "@/lib/http";
export type * from "./queries";
export type * from "./facets";
